import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Star, 
  Shield, 
  Bell, 
  ChevronRight,
  Copy,
  Check,
  MessageSquare,
  Headphones,
  Youtube,
  Megaphone
} from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { useTelegram } from '../../hooks';
import { authApi } from '../../api';
import toast from 'react-hot-toast';

const LEVELS = [
  { level: 1, name: 'Newcomer', color: 'from-gray-400 to-gray-500' },
  { level: 2, name: 'Bronze', color: 'from-orange-600 to-orange-400' },
  { level: 3, name: 'Silver', color: 'from-gray-300 to-gray-400' },
  { level: 4, name: 'Gold', color: 'from-yellow-500 to-yellow-400' },
  { level: 5, name: 'Platinum', color: 'from-cyan-400 to-blue-400' },
  { level: 6, name: 'Diamond', color: 'from-blue-400 to-purple-400' },
  { level: 7, name: 'Master', color: 'from-purple-500 to-pink-500' },
  { level: 8, name: 'Grandmaster', color: 'from-red-500 to-orange-500' },
  { level: 9, name: 'Legend', color: 'from-yellow-400 to-red-500' },
  { level: 10, name: 'Mythic', color: 'from-purple-600 to-pink-600' },
];

export function Profile() {
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [feedbackType, setFeedbackType] = useState<'suggestion' | 'bug' | 'other'>('suggestion');
  const [feedbackRating, setFeedbackRating] = useState(5);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const user = useAppStore((state) => state.user);
  const updateBalance = useAppStore((state) => state.updateBalance);
  const { hapticFeedback, hapticNotification } = useTelegram();

  // Show loading state instead of nothing when user is not available
  if (!user) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="space-y-6"
      >
        <div className="card p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-20 h-20 rounded-2xl bg-white/10 animate-pulse" />
            <div className="flex-1 space-y-2">
              <div className="h-6 bg-white/10 rounded w-32 animate-pulse" />
              <div className="h-4 bg-white/10 rounded w-24 animate-pulse" />
            </div>
          </div>
          <p className="text-center text-white/50 text-sm">Loading profile...</p>
        </div>
      </motion.div>
    );
  }

  const currentLevel = LEVELS.find((l) => l.level === user.level) || LEVELS[0];

  const copyUserId = async () => {
    await navigator.clipboard.writeText(user.telegramId.toString());
    setCopied(true);
    hapticNotification('success');
    setTimeout(() => setCopied(false), 2000);
  };

  const submitFeedback = async () => {
    if (!feedback.trim()) {
      toast.error('Please enter your feedback');
      return;
    }

    setSubmitting(true);
    hapticFeedback('medium');

    try {
      const response = await authApi.submitFeedback(feedbackRating, feedback.trim(), feedbackType);
      
      if (response.success) {
        toast.success(`Thank you for your feedback! +${response.reward} points`);
        // Update balance with server-returned value (authoritative)
        if (response.newBalance !== undefined) {
          updateBalance(response.newBalance);
        }
        setFeedback('');
        setFeedbackRating(5);
        setShowFeedback(false);
        hapticNotification('success');
      } else {
        toast.error('Failed to submit feedback');
      }
    } catch (error: any) {
      console.error('Submit feedback error:', error);
      toast.error(error?.response?.data?.error || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const stats = [
    { label: 'Total Earned', value: user.totalEarned?.toLocaleString() || '0' },
    { label: 'Balance', value: user.balance?.toLocaleString() || '0' },
    { label: 'Level', value: user.level?.toString() || '1' },
    { label: 'Days Active', value: user.streak?.toString() || '0' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      {/* Banned Banner */}
      {user.isBanned && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-500/20 border border-red-500/50 rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-500/30 flex items-center justify-center">
              <Shield className="w-5 h-5 text-red-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-red-400">Account Suspended</h3>
              <p className="text-white/60 text-sm">
                {user.banReason || 'Your account has been suspended due to a violation of our terms of service.'}
              </p>
              <p className="text-white/40 text-xs mt-1">
                Contact support if you believe this is a mistake.
                <Link
                  to="/support"
                  className="inline-block ml-2 px-2 py-1 bg-blue-500/80 text-white text-xs rounded hover:bg-blue-600 transition-colors font-semibold shadow"
                  style={{ verticalAlign: 'middle' }}
                >
                  Open Support Ticket
                </Link>
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Profile header */}
      <div className="card p-6">
        <div className="flex items-center gap-4 mb-6">
          {/* Avatar */}
          <div className={`relative w-20 h-20 rounded-2xl bg-gradient-to-br ${currentLevel.color} flex items-center justify-center`}>
            <span className="text-3xl font-bold">
              {user.firstName?.charAt(0).toUpperCase() || 'U'}
            </span>
            {/* Banned badge on avatar */}
            {user.isBanned && (
              <div className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-lg">
                BANNED
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold">{user.firstName || 'User'}</h2>
              {user.isBanned && (
                <span className="bg-red-500/20 text-red-400 text-xs font-medium px-2 py-0.5 rounded-full">
                  Banned
                </span>
              )}
            </div>
            {user.username && (
              <p className="text-white/60">@{user.username}</p>
            )}
            <div className="flex items-center gap-2 mt-1">
              <div className={`px-2 py-0.5 rounded-full bg-gradient-to-r ${currentLevel.color} text-xs font-medium`}>
                {currentLevel.name}
              </div>
              <span className="text-white/40 text-xs">Level {user.level}</span>
            </div>
          </div>
        </div>

        {/* User ID */}
        <div className="flex items-center justify-between p-3 rounded-xl bg-white/5">
          <div>
            <p className="text-white/40 text-xs">User ID</p>
            <p className="font-mono">{user.telegramId}</p>
          </div>
          <button
            onClick={copyUserId}
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
          >
            {copied ? (
              <Check className="w-4 h-4 text-green-400" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-4">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="card p-4 text-center"
          >
            <p className="text-white/50 text-xs mb-1">{stat.label}</p>
            <p className="font-bold text-lg">{stat.value}</p>
          </motion.div>
        ))}
      </div>

      {/* Achievements */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="card p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-400" />
            Achievements
          </h3>
          <span className="text-white/40 text-sm">
            {user.level || 0} unlocked
          </span>
        </div>

        <div className="grid grid-cols-4 gap-3">
          {[
            { icon: '🎯', name: 'First Claim' },
            { icon: '🔥', name: '7 Day Streak' },
            { icon: '💎', name: 'Big Spender' },
            { icon: '👥', name: '10 Referrals' },
            { icon: '⚡', name: 'Speed Demon' },
            { icon: '🎰', name: 'Lucky Winner' },
            { icon: '📺', name: 'Ad Master' },
            { icon: '🏆', name: 'Top 10' },
          ].map((achievement, index) => (
            <div
              key={index}
              className={`aspect-square rounded-xl flex items-center justify-center text-2xl ${
                index < (user.level || 0)
                  ? 'bg-white/10'
                  : 'bg-white/5 opacity-30 grayscale'
              }`}
            >
              {achievement.icon}
            </div>
          ))}
        </div>
      </motion.div>

      {/* Settings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="card divide-y divide-white/10"
      >
        <button className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors">
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-white/60" />
            <span>Notifications</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-white/40 text-sm">On</span>
            <ChevronRight className="w-4 h-4 text-white/40" />
          </div>
        </button>

        <button className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-white/60" />
            <span>KYC Status</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-sm ${
              user.kycVerified ? 'text-green-400' : 'text-white/40'
            }`}>
              {user.kycVerified ? 'Verified' : 'Not verified'}
            </span>
            <ChevronRight className="w-4 h-4 text-white/40" />
          </div>
        </button>

        <button
          onClick={() => setShowFeedback(true)}
          className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center gap-3">
            <MessageSquare className="w-5 h-5 text-white/60" />
            <span>Send Feedback</span>
          </div>
          <ChevronRight className="w-4 h-4 text-white/40" />
        </button>

        <Link
          to="/advertiser"
          className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Megaphone className="w-5 h-5 text-primary" />
            <div>
              <span className="text-white">Advertise with Us</span>
              <p className="text-xs text-white/40">Promote your channel or project</p>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-white/40" />
        </Link>

        <Link
          to="/support"
          className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Headphones className="w-5 h-5 text-white/60" />
            <span>Support Tickets</span>
          </div>
          <ChevronRight className="w-4 h-4 text-white/40" />
        </Link>

        <a
          href="https://www.youtube.com/@gemifaucet"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Youtube className="w-5 h-5 text-red-500" />
            <span>YouTube Channel</span>
          </div>
          <ChevronRight className="w-4 h-4 text-white/40" />
        </a>
      </motion.div>

      {/* Feedback modal */}
      <AnimatePresence>
        {showFeedback && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
            onClick={() => setShowFeedback(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 50 }}
              className="card p-6 max-w-md w-full max-h-[80vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="font-bold text-lg mb-4">Send Feedback</h3>

              {/* Type selector */}
              <div className="flex gap-2 mb-4">
                {['suggestion', 'bug', 'other'].map((type) => (
                  <button
                    key={type}
                    onClick={() => setFeedbackType(type as typeof feedbackType)}
                    className={`flex-1 py-2 rounded-lg text-sm capitalize transition-colors ${
                      feedbackType === type
                        ? 'bg-primary text-white'
                        : 'bg-white/10 hover:bg-white/20'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>

              {/* Rating selector */}
              <div className="mb-4">
                <p className="text-sm text-white/60 mb-2">Rating</p>
                <div className="flex gap-2 justify-center">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setFeedbackRating(star)}
                      className="p-1 transition-transform hover:scale-110"
                    >
                      <Star
                        className={`w-8 h-8 ${
                          star <= feedbackRating
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-white/30'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Feedback input */}
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Tell us what you think..."
                rows={5}
                className="w-full bg-white/10 border border-white/10 rounded-xl px-4 py-3 resize-none focus:outline-none focus:border-primary"
              />

              {/* Actions */}
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => setShowFeedback(false)}
                  className="flex-1 py-3 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={submitFeedback}
                  disabled={submitting}
                  className="flex-1 py-3 rounded-xl btn-primary flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    'Submit'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* App info */}
      <div className="text-center text-white/30 text-xs py-4">
        <p>Gemifaucet v1.0.0</p>
        <p className="mt-1">© 2024 Gemifaucet. All rights reserved.</p>
      </div>
    </motion.div>
  );
}
